import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { Button } from 'primereact/button'

import { doesSupportSmartSchedule } from '../../common/domain/charging-schedules'
import { SButtonsGroupContainer } from '../../common/styles/Widget'
import canEditChargePoint from '../../lib/can-edit-charge-point'
import { isUserappDomain } from '../../lib/currentDomain'
import { GreenChargingBadge } from '../../lib/green-charging-badge'
import { WidgetLayout } from '../../lib/widget-layout'
import { ChargePoint, useSmartChargingScheduleGetByChargePointQuery } from '../../common/redux/api/web-api'
import useText from '../../common/hooks/useText'
import { useSessionUser } from '../../common/hooks/useSessionUser'

const SDisabledOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: rgba(212, 212, 212, 0.5);
    z-index: 1;
`

interface OpenSmartScheduleButtonProps {
    chargePoint: ChargePoint
    existing: boolean
    isGreenChargingEnabled?: boolean
    loading?: boolean
    enabled: boolean
}

const OpenSmartScheduleButton = ({
    chargePoint,
    existing,
    isGreenChargingEnabled,
    loading,
    enabled,
}: OpenSmartScheduleButtonProps) => {
    const text = useText()
    const hasSmartScheduleSupport = useMemo(() => doesSupportSmartSchedule(chargePoint), [chargePoint])
    const link = `${isUserappDomain ? '/remotecontrol' : '/chargepoints'}/${chargePoint.id}/schedule`
    const disabled = isGreenChargingEnabled || !hasSmartScheduleSupport

    const buttonLabel = () => {
        if (!existing) {
            return text('lblCreateScheduleLink')
        }

        return text(enabled ? 'btnEditSchedule' : 'btnViewSchedule')
    }

    const buttonProps = {
        label: buttonLabel(),
        icon: 'pi pi-calendar',
        'data-testid': 'link-to-smart',
    }

    return disabled || loading ? (
        <Button type="button" {...buttonProps} loading={loading} disabled={disabled} />
    ) : (
        <Link
            aria-label={buttonProps.label}
            className="p-button p-component"
            data-testid={buttonProps['data-testid']}
            to={link}
        >
            <span aria-hidden="true" className="p-button-icon p-c p-button-icon-left pi pi-calendar" />
            <span className="p-button-label p-c">{buttonProps.label}</span>
        </Link>
    )
}

export const ChargePointScheduleDetailsWidget = ({
    chargePoint,
    isGreenChargingEnabled,
    loading,
}: ChargePointScheduleDetailsWidgetProps): React.ReactElement => {
    const text = useText()
    const user = useSessionUser()
    const userHasEditRights = useMemo(() => canEditChargePoint(chargePoint, user), [chargePoint, user])

    const { data: schedules } = useSmartChargingScheduleGetByChargePointQuery({ chargePointId: chargePoint.id || '' })

    return (
        <>
            {userHasEditRights && (
                <WidgetLayout
                    title={text('lblSchedulingWidget')}
                    data-testid="smart-scheduling-widget"
                    loadingStatus={loading}
                    actionButtons={
                        userHasEditRights && (
                            <SButtonsGroupContainer className="action-btns">
                                <OpenSmartScheduleButton
                                    chargePoint={chargePoint}
                                    existing={!!schedules?.length}
                                    isGreenChargingEnabled={isGreenChargingEnabled}
                                    loading={loading}
                                    enabled={userHasEditRights}
                                />
                            </SButtonsGroupContainer>
                        )
                    }
                >
                    {loading ? (
                        <div className="shimmer-container shimmer-medium"></div>
                    ) : isGreenChargingEnabled ? (
                        <div>
                            <GreenChargingBadge />
                            <SDisabledOverlay />
                        </div>
                    ) : null}
                </WidgetLayout>
            )}
        </>
    )
}

export type ChargePointScheduleDetailsWidgetProps = {
    chargePoint: ChargePoint
    loading?: boolean
    isGreenChargingEnabled?: boolean
}
